// A pasted textbook unit's Classroom procedure holds all its class periods, each under the
// heading the server writes (verbaschola_lesson_ai_plan_periods.period_heading). Its steps
// are numbered, and move, only within their own period.
const PERIOD_HEADING = /^## Period \d+ of \d+ \((\d+) min\)$/;

const isHeading = (line: string) => !/^\s/.test(line) && (
    /^(?:#{1,6}\s+|\d+[.)]\s+|step\s+\d+\b)/i.test(line)
    || /^(?:\d+\s*[-–]\s*)?\d+\s*(?:min(?:utes)?|分钟)(?:\b|(?=[:：\s]))/i.test(line)
    || /^.{1,80}\(\d+\s*min(?:utes)?\)\s*:?\s*$/i.test(line));

// The text before the first period (the introduction and any steps), then each period: its
// heading, any text before its first step, and its steps.
interface ProcedurePart { heading: string | null; lead: string[]; steps: string[] }

function procedureParts(body: string | null | undefined): ProcedurePart[] {
    const parts: ProcedurePart[] = [{ heading: null, lead: [], steps: [] }];
    for (const line of (body ?? '').split(/\r?\n/)) {
        const part = parts[parts.length - 1];
        if (PERIOD_HEADING.test(line.trim())) parts.push({ heading: line.trim(), lead: [], steps: [] });
        else if (isHeading(line)) part.steps.push(line);
        else if (part.steps.length) part.steps[part.steps.length - 1] += `\n${line}`;
        else part.lead.push(line);
    }
    return parts;
}

export interface PlanProcedure {
    introduction: string;
    steps: string[];
    // The class period heading each step belongs to, or null outside periods.
    periods: (string | null)[];
    // Text a period prints before its first step, by heading.
    periodNotes: Record<string, string>;
}

export function parsePlanProcedure(body: string | null | undefined): PlanProcedure {
    const parts = procedureParts(body);
    if (parts.length === 1 && !parts[0].steps.length) {
        // No stage headings: each line is a step.
        const steps = parts[0].lead.filter(line => line.trim());
        return { introduction: '', steps, periods: steps.map(() => null), periodNotes: {} };
    }
    const periodNotes: Record<string, string> = {};
    for (const part of parts) {
        const note = part.lead.join('\n').trim();
        if (part.heading && note) periodNotes[part.heading] = note;
    }
    return {
        introduction: parts[0].lead.join('\n').trim(),
        steps: parts.flatMap(part => part.steps.map(step => step.trimEnd())),
        periods: parts.flatMap(part => part.steps.map(() => part.heading)),
        periodNotes,
    };
}

export function planProcedureSteps(body: string | null | undefined): string[] {
    return parsePlanProcedure(body).steps;
}

// The minutes of each class period the procedure holds (none for a one-class procedure).
export function procedurePeriodMinutes(body: string | null | undefined): number[] {
    return procedureParts(body).flatMap(part => {
        const match = part.heading?.match(PERIOD_HEADING);
        return match ? [Number(match[1])] : [];
    });
}

function numbered(steps: string[]): string[] {
    return steps.map((step, index) => step
        .replace(/^\d+([.)])\s+/, `${index + 1}$1 `)
        .replace(/^(step\s+)\d+\b/i, `$1${index + 1}`));
}

export function reorderPlanProcedureSteps(body: string, from: number, to: number): string {
    const { introduction, steps, periods } = parsePlanProcedure(body);
    if (!Number.isInteger(from) || !Number.isInteger(to) || from < 0 || to < 0 || from >= steps.length || to >= steps.length || from === to
        || periods[from] !== periods[to]) return body;
    const parts = procedureParts(body);
    if (parts.length === 1) {
        const reordered = [...steps];
        const [moved] = reordered.splice(from, 1);
        reordered.splice(to, 0, moved);
        const joined = numbered(reordered).join('\n\n');
        return introduction ? `${introduction}\n\n${joined}` : joined;
    }
    // Move within the step's own period, then renumber that period's steps from 1.
    let offset = 0;
    const blocks: string[] = [];
    for (const part of parts) {
        let partSteps = part.steps.map(step => step.trimEnd());
        if (from >= offset && from < offset + partSteps.length) {
            const [moved] = partSteps.splice(from - offset, 1);
            partSteps.splice(to - offset, 0, moved);
            partSteps = numbered(partSteps);
        }
        offset += part.steps.length;
        const lead = part.lead.join('\n').trim();
        const text = [lead, ...partSteps].filter(Boolean).join('\n\n');
        if (part.heading) blocks.push(text ? `${part.heading}\n${text}` : part.heading);
        else if (text) blocks.push(text);
    }
    return blocks.join('\n\n');
}

export function reorderedOpenSteps(open: number[], from: number, to: number): number[] {
    return open.map(index => index === from ? to
        : from < to && index > from && index <= to ? index - 1
        : from > to && index >= to && index < from ? index + 1 : index);
}
